// The entity, business unit and financial year a property's documents belong to on a date, shown
// read-only in a transaction screen's header band (settings cleanup C15). CRS never asks the user to
// pick these: they follow the property and the date, so the header has no selectors for them
// (UI.LAYOUT.SHOW_ENTITY_BU / SHOW_FINANCIAL_YEAR are off).

import { useEffect, useState } from 'react';
import { get } from 'api/client/axiosClient';
import { buildEndpointWithQuery } from 'api/endpoints/base.endpoints';
import type { ApiResponse } from 'api/types/common.types';
import type { ReportHeaderItem } from 'components/reports/ReportHeader';

interface PropertyScope {
  entityName?: string | null;
  businessUnitName?: string | null;
  financialYearName?: string | null;
}

/** Read-only header items: Entity, Business unit, Financial year. Empty until the property is known. */
export const usePropertyScopeItems = (propertyId: string, date?: string): ReportHeaderItem[] => {
  const [scope, setScope] = useState<PropertyScope | null>(null);

  useEffect(() => {
    if (!propertyId) { setScope(null); return; }
    let cancelled = false;
    get<ApiResponse<PropertyScope>>(buildEndpointWithQuery('/org/pickers/property-scope', { propertyId, date: date || undefined }))
      .then((res) => { if (!cancelled) setScope(res.data.data ?? null); })
      .catch(() => { if (!cancelled) setScope(null); });
    return () => { cancelled = true; };
  }, [propertyId, date]);

  if (!scope) return [];
  return [
    { label: 'Entity', value: scope.entityName || 'Not linked', icon: 'home', title: 'The legal entity of the property' },
    { label: 'Business unit', value: scope.businessUnitName || 'Not linked', icon: 'folder', title: 'The property\'s business unit' },
    { label: 'FY', value: scope.financialYearName || 'Not set up', icon: 'calendar', title: 'The entity\'s financial year for this date' },
  ];
};
